import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionCard } from './SectionCard'
import { useInterventions7Days } from '@/hooks/useDashboard'
import { cn } from '@/lib/utils'
import { formatChartDayLabel, formatChartRange } from './interventionsChartDate'
import { getChartYAxisScale } from './interventionsChartScale'

const X_AXIS_HEIGHT = 45
const VALUE_LABEL_HEIGHT = 16
const VALUE_LABEL_GAP = 4
const CHART_TOP_PADDING = 4
const PLOT_INSET_TOP = VALUE_LABEL_HEIGHT + VALUE_LABEL_GAP + CHART_TOP_PADDING
const CHART_INSET_LEFT = 48
const CHART_INSET_RIGHT = 24
const COLUMN_GAP = 16
const BAR_LABEL_GAP = 9
const BAR_WIDTH = 32

function InterventionsChartIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
    >
      <path
        d='M8 13.3334V6.6667M12 13.3334V2.6667M4 13.3334V10.6667'
        stroke='#FF5A1F'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

function plotHeightFor(chartHeight: number): number {
  return Math.max(
    0,
    chartHeight - X_AXIS_HEIGHT - PLOT_INSET_TOP - BAR_LABEL_GAP
  )
}

type InterventionsBarChartProps = {
  labels: string[]
  values: number[]
  locale: string
}

function InterventionsBarChart({
  labels,
  values,
  locale,
}: InterventionsBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 })
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const update = () =>
      setChartSize({ width: node.clientWidth, height: node.clientHeight })
    update()

    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const now = new Date()
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const todayIndex = labels.indexOf(todayIso)
  const defaultActiveIndex =
    todayIndex >= 0 ? todayIndex : values.indexOf(Math.max(...values))
  const { max: yMax, ticks } = getChartYAxisScale(values)
  const chartHeight = chartSize.height
  const plotHeight = plotHeightFor(chartHeight)

  const yToPx = (tick: number) =>
    PLOT_INSET_TOP + plotHeight - (tick / yMax) * plotHeight

  return (
    <div ref={containerRef} className='relative min-h-60 w-full flex-1'>
      {/* Grid lines only — stretch full width */}
      {chartSize.width > 0 && chartHeight > 0 && (
        <svg
          className='pointer-events-none absolute inset-0 h-full w-full'
          viewBox={`0 0 ${chartSize.width} ${chartHeight}`}
          preserveAspectRatio='none'
          aria-hidden
        >
          {ticks.map(tick => {
            const y = yToPx(tick)
            return (
              <g key={tick}>
                <text
                  x={40}
                  y={y}
                  textAnchor='end'
                  dominantBaseline='middle'
                  className='fill-[#757575] font-sans text-[12px] font-semibold'
                >
                  {tick}
                </text>
                <line
                  x1={CHART_INSET_LEFT}
                  y1={y}
                  x2={chartSize.width - CHART_INSET_RIGHT}
                  y2={y}
                  className='stroke-[#f4f5f8]'
                  strokeWidth={1}
                />
              </g>
            )
          })}
        </svg>
      )}

      {/* Bars + labels share one flex column layout */}
      <div
        className='absolute inset-0 flex flex-col'
        style={{
          paddingTop: PLOT_INSET_TOP,
          paddingLeft: CHART_INSET_LEFT,
          paddingRight: CHART_INSET_RIGHT,
          gap: BAR_LABEL_GAP,
        }}
      >
        <div
          className='flex min-h-0 flex-1 items-end'
          style={{ gap: COLUMN_GAP }}
        >
          {labels.map((label, index) => {
            const value = values[index] ?? 0
            const barHeight = Math.max(
              value > 0 ? 4 : 0,
              (value / yMax) * plotHeight
            )
            const isHovered = hoveredIndex === index
            const isHighlighted =
              hoveredIndex === null ? index === defaultActiveIndex : isHovered
            const isDimmed = hoveredIndex !== null && !isHovered
            const labelBottom = barHeight + VALUE_LABEL_GAP

            return (
              <div
                key={label}
                className='relative flex min-w-0 flex-1 justify-center'
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <span
                  className={cn(
                    'pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center font-sans text-xs font-medium leading-4 text-[#171a20]',
                    isDimmed && 'opacity-60'
                  )}
                  style={{ bottom: labelBottom }}
                >
                  {value}
                </span>
                <button
                  type='button'
                  aria-label={`${value}`}
                  className={cn(
                    'shrink-0 rounded-t-lg transition-colors duration-150 ease-out',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isHighlighted
                      ? 'bg-[#ff5a1f] shadow-[0_0_16px_4px_rgba(255,216,191,0.8)]'
                      : 'bg-[#f4f5f8]',
                    isDimmed && 'opacity-60'
                  )}
                  style={{ width: BAR_WIDTH, height: barHeight }}
                  onFocus={() => setHoveredIndex(index)}
                  onBlur={() => setHoveredIndex(null)}
                />
              </div>
            )
          })}
        </div>

        <div
          className='flex shrink-0 items-start'
          style={{ height: X_AXIS_HEIGHT, gap: COLUMN_GAP }}
        >
          {labels.map((label, index) => {
            const { weekdayLine, dateLine } = formatChartDayLabel(label, locale)
            const isActive =
              hoveredIndex === null
                ? index === defaultActiveIndex
                : index === hoveredIndex
            return (
              <div
                key={`${label}-axis`}
                className={cn(
                  'min-w-0 flex-1 text-center font-sans text-xs font-medium leading-4 text-[#3d434e]',
                  isActive && 'text-[#ff5a1f]'
                )}
              >
                <span className='block'>{weekdayLine}</span>
                <span className='block'>{dateLine}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function InterventionsChart() {
  const { t, i18n } = useTranslation('dashboard')
  const { data, isLoading, isError } = useInterventions7Days()
  const labels = data?.data?.labels ?? []
  const values = data?.data?.series?.[0]?.data ?? []
  const total = values.reduce((a, b) => a + b, 0)
  const dateRange = formatChartRange(labels, i18n.language)

  let body: React.ReactNode
  if (isLoading) {
    body = <Skeleton className='min-h-60 w-full flex-1' />
  } else if (isError) {
    body = (
      <p className='flex flex-1 items-center justify-center py-12 text-center text-sm text-destructive'>
        {t('interventionsChart.unableToLoad')}
      </p>
    )
  } else if (labels.length === 0 || total === 0) {
    body = (
      <p className='flex flex-1 items-center justify-center py-12 text-center text-sm text-muted-foreground'>
        {t('interventionsChart.empty')}
      </p>
    )
  } else {
    body = (
      <InterventionsBarChart
        labels={labels}
        values={values}
        locale={i18n.language}
      />
    )
  }

  return (
    <SectionCard
      variant='chart'
      title={t('interventionsChart.title')}
      className='h-full'
      contentClassName='h-[292px] min-h-0'
      headerIcon={<InterventionsChartIcon />}
      headerIconClassName='bg-[#fff1ea]'
      headerRight={dateRange}
    >
      {body}
    </SectionCard>
  )
}

export default InterventionsChart
